import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Film } from "lucide-react";
import { toast } from "sonner";

export default function Register() {
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/auth/register", form);
      setUser(data);
      toast.success("Account created");
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#090A0F] px-6 grain relative">
      <div className="w-full max-w-md glass rounded-2xl p-8 gold-glow fade-up" data-testid="register-card">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <Film className="w-6 h-6 text-[#D4AF37]" />
          <span className="font-display text-2xl text-white tracking-wide">PROPMOTION</span>
        </Link>
        <h1 className="font-display text-3xl text-white text-center mb-1">Create your account</h1>
        <p className="text-sm text-slate-400 text-center mb-6">Start producing cinematic property films</p>

        {error && <p className="text-sm text-red-400 mb-4 text-center" data-testid="register-error">{error}</p>}

        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-slate-300">Full name</Label>
            <Input value={form.name} onChange={set("name")} required
              className="mt-1.5 bg-[#12141D] border-slate-700 text-white" data-testid="register-name" />
          </div>
          <div>
            <Label className="text-slate-300">Email</Label>
            <Input type="email" value={form.email} onChange={set("email")} required
              className="mt-1.5 bg-[#12141D] border-slate-700 text-white" data-testid="register-email" />
          </div>
          <div>
            <Label className="text-slate-300">Password</Label>
            <Input type="password" value={form.password} onChange={set("password")} required minLength={6}
              className="mt-1.5 bg-[#12141D] border-slate-700 text-white" data-testid="register-password" />
          </div>
          <Button type="submit" disabled={busy}
            className="w-full bg-[#D4AF37] text-black hover:bg-[#E2C044] rounded-full" data-testid="register-submit">
            {busy ? "Creating..." : "Create Account"}
          </Button>
        </form>

        <p className="text-sm text-slate-400 text-center mt-6">
          Already have an account? <Link to="/login" className="text-[#D4AF37] hover:underline" data-testid="to-login">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
